import { useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { useLicenseActivityQuery } from '../../../hooks/useLicenseActivityQuery';
import { LicenseHistoryAction } from '../../../types/manageLicenses';
import type { ManageLicenseListDto, LicenseActionHistoryDto } from '../../../types/manageLicenses';

interface Props {
  license: ManageLicenseListDto;
  onClose: () => void;
}

const PAGE_SIZE = 25;

function actionLabel(a: LicenseHistoryAction): string {
  return LicenseHistoryAction[a] ?? String(a);
}

function actionColour(a: LicenseHistoryAction): string {
  switch (a) {
    case LicenseHistoryAction.Revoke:
      return 'bg-red-100 text-red-800';
    case LicenseHistoryAction.Suspend:
      return 'bg-amber-100 text-amber-800';
    case LicenseHistoryAction.Reactivate:
      return 'bg-green-100 text-green-800';
    case LicenseHistoryAction.Extend:
      return 'bg-blue-100 text-blue-800';
    case LicenseHistoryAction.BulkAssign:
      return 'bg-neutral-100 text-neutral-800';
    case LicenseHistoryAction.TerminateSessions:
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-neutral-100 text-neutral-700';
  }
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function MetadataSummary({ row }: { row: LicenseActionHistoryDto }) {
  const { t } = useTranslation();
  if (
    row.action === LicenseHistoryAction.Extend &&
    row.previousExpirationDate !== undefined
  ) {
    const from = row.previousExpirationDate
      ? new Date(row.previousExpirationDate).toLocaleDateString()
      : t('App.Common.Perpetual');
    const to = row.newExpirationDate
      ? new Date(row.newExpirationDate).toLocaleDateString()
      : t('App.Common.Perpetual');
    return <span className="text-xs text-neutral-600">{t('App.Admin.Licenses.LicenseDrawerActivityTab.ExpirationChange', { from, to })}</span>;
  }
  if (row.action === LicenseHistoryAction.Suspend && row.metadata) {
    try {
      const m = JSON.parse(row.metadata) as Record<string, unknown>;
      if (typeof m.terminatedCount === 'number') {
        return (
          <span className="text-xs text-neutral-600">
            {t('App.Admin.Licenses.LicenseDrawerActivityTab.TerminatedSessions', { count: m.terminatedCount })}
          </span>
        );
      }
    } catch {
      // ignore parse errors
    }
  }
  if (row.action === LicenseHistoryAction.BulkAssign && row.metadata) {
    try {
      const m = JSON.parse(row.metadata) as Record<string, unknown>;
      return (
        <span className="text-xs text-neutral-600">
          {t('App.Admin.Licenses.LicenseDrawerActivityTab.BulkAssignMeta', { type: String(m.licenseType), devices: String(m.maxConcurrentDevices) })}
        </span>
      );
    } catch {
      // ignore parse errors
    }
  }
  return null;
}

export default function LicenseDrawerActivityTab({ license, onClose: _onClose }: Props) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const { data, isLoading } = useLicenseActivityQuery(
    license.id,
    page * PAGE_SIZE,
    PAGE_SIZE,
  );

  if (isLoading) return <div className="p-4 text-sm text-neutral-500">{t('App.Admin.Licenses.LicenseDrawerActivityTab.Loading')}</div>;

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div className="p-4 flex flex-col gap-4">
      {items.length === 0 && (
        <p className="text-sm text-neutral-500">{t('App.Admin.Licenses.LicenseDrawerActivityTab.NoActivity')}</p>
      )}

      {items.length > 0 && (
        <ol className="flex flex-col gap-3">
          {items.map((row) => (
            <li key={row.id} className="flex gap-3 items-start">
              {/* Action chip */}
              <span
                className={`shrink-0 mt-0.5 px-2 py-0.5 rounded text-xs font-medium ${actionColour(row.action)}`}
              >
                {actionLabel(row.action)}
              </span>

              {/* Details */}
              <div className="flex flex-col gap-0.5 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-neutral-700 font-medium">
                    {row.actorUserName || row.actorUserId}
                  </span>
                  <span
                    className="text-xs text-neutral-400 cursor-default"
                    title={new Date(row.timestamp).toLocaleString()}
                  >
                    {relativeTime(row.timestamp)}
                  </span>
                </div>
                {row.reason && (
                  <p className="text-xs text-neutral-600 break-words">{row.reason}</p>
                )}
                <MetadataSummary row={row} />
              </div>
            </li>
          ))}
        </ol>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between pt-2 border-t border-neutral-100">
        <span className="text-xs text-neutral-500">
          {t('App.Admin.Licenses.LicenseDrawerActivityTab.PageSummary', { page: page + 1, totalPages, totalCount })}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            className="px-2 py-1 text-xs border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t('App.Admin.Licenses.LicenseDrawerActivityTab.PrevButton')}
          </button>
          <button
            type="button"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            className="px-2 py-1 text-xs border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t('App.Admin.Licenses.LicenseDrawerActivityTab.NextButton')}
          </button>
        </div>
      </div>
    </div>
  );
}
